import { useEffect, useRef, useState } from "react";
import { getAllActiveStaffMeetings } from "../../../../services/AppService";

import { IoClose, IoTrashOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";

const AddStaffAgendaForm = ({ onSubmit, editData, onClose, open }) => {
    const { t } = useTranslation();

    const [meetingId, setMeetingId] = useState("");
    const [meetings, setMeetings] = useState([]);

    const [agendaRows, setAgendaRows] = useState([
        { id: Date.now(), agendaDescription: "", file: null, fileName: "" }
    ])

    const [formData, setFormData] = useState({
        name: "",
        meetingId: "",
        active: true
    });

    const [preview, setPreview] = useState(null);
    const [fileName, setFileName] = useState(null);
    const [image, setImage] = useState(null);
    const [error, setError] = useState(null);

    const fileInputRef = useRef(null);


    // 1. Initial Load: Fetch Masters
    useEffect(() => {
        getAllActiveStaffMeetings().then(setMeetings).catch(console.error);
    }, []);


    useEffect(() => {
        if (editData && open) {
            setMeetingId(editData.meetingId || "");

            if (editData.agendaItems) {
                setAgendaRows(editData.agendaItems.map(item => ({
                    id: item.id,
                    agendaDescription: item.agendaDescription || "",
                    file: null,
                    fileName: item.attachmentName || ""
                })))
            }
        } else {
            setMeetingId("");
            setAgendaRows([{ id: Date.now(), agendaDescription: "", file: null, fileName: "" }])
        }
    }, [editData, open]);

    const addRow = () => {
        setAgendaRows([...agendaRows, { id: Date.now(), agendaDescription: "", file: null, fileName: "" }])
    }

    const removeRow = (id) => {
        if (agendaRows.length > 1) {
            setAgendaRows(agendaRows.filter(row => row.id !== id));
        }
    }

    const handleRowChange = (id, field, value) => {
        setAgendaRows(prevRows => prevRows.map(row =>
            row.id === id ? { ...row, [field]: value } : row
        ));
    };

    const handleFileChange = (id, e) => {
        const file = e.target.files[0];
        if (file) {
            setAgendaRows(prevRows => prevRows.map(row =>
                row.id === id ? { ...row, file: file, fileName: file.name } : row
            ));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Convert ID strings to Numbers for backend compatibility
        const finalValue = (name === "meetingId") ? Number(value) :
            (name === "active" ? value === "true" : value);

        setFormData(prev => ({ ...prev, [name]: finalValue }));
    };

    // const handleFileChange = (e) => {
    //     const file = e.target.files[0];
    //     if (file) {
    //         setImage(file);
    //         setPreview(URL.createObjectURL(file));
    //         setFileName(file.name);
    //     }
    // };

    const handleRemoveImage = () => {
        setImage(null);
        setPreview(null);
        setFileName(null);
        // const fileInput = document.querySelector('input[name="photo"]');
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // 1. Verify we have a meetingId selected
        if (!meetingId) {
            setError("Please select a meeting.");
            return;
        }

        // 1. Prepare the JSON DTO
        const agendaDto = {
            meetingId: Number(meetingId),
            items: agendaRows.map(row => ({
                id: typeof row.id === 'number' && row.id > 1000000000000 ? null : row.id,
                agendaDescription: row.agendaDescription.trim()
            }))
        };

        const finalFormData = new FormData();

        // 2. Append JSON DTO
        finalFormData.append(
            "agenda",
            new Blob([JSON.stringify(agendaDto)], { type: "application/json" })
        );

        // 3. Append Files in same order as items
        agendaRows.forEach((row) => {
            if (row.file) {
                finalFormData.append("files", row.file);
            } else {
                // Must append an empty blob to keep the List<MultipartFile> indices aligned with the items
                finalFormData.append("files", new Blob([], { type: "application/octet-stream" }));
            }
        });

        onSubmit(finalFormData);
    };

    // const formFields = [
    //     { name: "name", label: "Meeting Title", type: "text", placeholder: "Enter Title" },
    //     { name: "meetingId", label: "Meeting", type: "text", placeholder: "Select Meeting" },
    // ];

    if (!open) return null;

    return (
        <div className="staff-modal-overlay">
            <div className="staff-modal" style={{ width: '900px', maxWidth: '95%' }}>
                <div className="staff-modal-header">
                    <div className="staff-modal-title">
                        {editData ? t('pageTitles.editAgenda') : t('pageTitles.addAgenda')}
                    </div>
                    <button className="admin-modal-close" onClick={onClose}>
                        <IoClose />
                    </button>
                </div>

                <div className="staff-form-body">
                    <form onSubmit={handleSubmit}>
                        {/* MEETING SELECT */}
                        <div className="form-group mb-4">
                            <label className="staff-modal-label">{t('forms.agenda.meeting')} <span className="text-red-500">*</span></label>
                            <select
                                value={meetingId}
                                onChange={(e) => setMeetingId(e.target.value)}
                                className="staff-modal-input"
                                required
                            >
                                <option value="">Select Meeting</option>
                                {meetings.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </select>
                        </div>

                        <div className="mb-2">
                            <button type="button" onClick={addRow} className="staff-btn-save text-sm py-1">
                                {t('forms.agenda.addAgenda')}
                            </button>
                        </div>

                        {/* DYNAMIC TABLE */}
                        <table className="w-full border text-sm agenda-table">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="border p-2 w-1/2">{t('forms.agenda.agenda')}</th>
                                    <th className="border p-2 w-1/3">{t('tables.attachmentDesc')}</th>
                                    <th className="border p-2">{t('common.delete')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {agendaRows.map((row) => (
                                    <tr key={row.id}>
                                        <td className="border p-2">
                                            <textarea
                                                className="staff-modal-input w-full h-20"
                                                placeholder="Enter Agenda"
                                                value={row.agendaDescription}
                                                onChange={(e) => handleRowChange(row.id, 'agendaDescription', e.target.value)}
                                                required
                                            />
                                        </td>
                                        <td className="border p-2">
                                            <div className="flex flex-col gap-1">
                                                <input
                                                    type="file"
                                                    onChange={(e) => handleFileChange(row.id, e)}
                                                    className="text-xs"
                                                />
                                                {row.fileName && <span className="text-blue-600 text-xs">{row.fileName}</span>}
                                            </div>
                                        </td>
                                        <td className="border p-2 text-center">
                                            <button
                                                type="button"
                                                onClick={() => removeRow(row.id)}
                                                className="text-red-500 hover:text-red-700"
                                            >
                                                <IoTrashOutline size={20} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="flex justify-center mt-6">
                            <button type="submit" className="staff-btn-save px-10">{editData ? t('common.update') : t('common.save')}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddStaffAgendaForm;

